// TODO move this to a component pkg

import { ID } from "@nova/constants"
import classNames from "classnames"
import {
    forwardRef,
    KeyboardEvent,
    MouseEvent,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react"
import { ConfirmControls } from "@nova/component-confirm-controls"
import { useTranslation } from "@nova/use-translation"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"

type RenamableTextProps = {
    Tag: "h1" | "h2" | "h3" | "h4" | "h5" | "small"

    title: string
    id: ID
    isRenaming: boolean

    /**
     * Whether the `title` prop should be a placeholder. If `undefined`,
     * title will be real, selectable text.
     */
    initialTitleIsPlaceholder?: boolean

    /**
     * If background is bg-gray-700, the hover state for the confirm/cancel
     * controls will blend in. Set to `true` to brighten the controls hover
     * state in this special circumstance
     * @default false
     */
    backgroundIsDark?: boolean

    /**
     * Whether the background of the text should highlight on hover.
     * @default false
     */
    shouldHighlightOnHover?: boolean

    /**
     * Whether the text should be selectable while not in "renaming" mode.
     * @default false
     */
    shouldMakeTextSelectable?: boolean

    /**
     * Whether it should fill the available width.
     * @default true
     */
    isFullWidth?: boolean

    /**
     * An important height value should be passed in depending on the tag.
     * @example "!h-6"
     */
    className?: string

    underlineClassNames?: (isRenaming: boolean) => string

    /**
     * By default, the input field's onChange event will trim any leading
     * spaces. This prop lets you provide an additional function to sanitize
     * the contents.
     */
    sanitizer?: (value: string) => string | null

    onClick?: (e: MouseEvent) => void
    onConfirmRenaming?: (id: ID, name: string) => void
    onCancelRenaming?: () => void
    onResetName?: () => void

    maxLines?: number
    suffix?: string
    saveOnBlur?: boolean
}

const RenamableText = forwardRef(
    (
        {
            Tag,
            title,
            id,
            isRenaming,
            initialTitleIsPlaceholder = false,
            backgroundIsDark = false,
            className,
            underlineClassNames,
            isFullWidth = true,
            shouldHighlightOnHover = false,
            shouldMakeTextSelectable = false,
            sanitizer,
            onClick,
            onConfirmRenaming,
            onCancelRenaming,
            onResetName,
            maxLines = 1,
            suffix = "",
            saveOnBlur = true,
        }: RenamableTextProps,
        ref
    ) => {
        const { t } = useTranslation()

        const inputRef = useRef<HTMLInputElement>(null)
        const [renamedTitle, setRenamedTitle] = useState(initialTitleIsPlaceholder ? "" : title)
        const [isHovering, setIsHovering] = useState(false)

        useImperativeHandle(ref, () => ({
            focus: () => {
                inputRef.current?.focus()
            },
        }))

        const confirmRenaming = useCallback(() => {
            const sanitizedTitle = renamedTitle.trimEnd()
            if (sanitizedTitle.length === 0) return
            if (sanitizedTitle === title) {
                onCancelRenaming?.()
                return
            }
            onConfirmRenaming?.(id, sanitizedTitle)
        }, [onConfirmRenaming, onCancelRenaming, renamedTitle, id, title])
        const confirmRenamingRef = useRef(confirmRenaming)
        useEffect(() => {
            confirmRenamingRef.current = confirmRenaming
        }, [confirmRenaming])

        const handleKeyDown = useCallback(
            (e: KeyboardEvent) => {
                e.stopPropagation()

                if (e.key === "Enter" && isRenaming) {
                    confirmRenaming()
                } else if (e.key === "Escape" && isRenaming) {
                    onCancelRenaming?.()
                    setRenamedTitle(title)
                }
            },
            [confirmRenaming, title, isRenaming, onCancelRenaming]
        )

        useEffect(() => {
            setRenamedTitle(initialTitleIsPlaceholder ? "" : title)
        }, [title, initialTitleIsPlaceholder, isRenaming])

        /**
         * When this component *stops* being renamed, confirm the changes.
         * This will handle all cases of clicking away such as parent component
         * dismount, change of input field selection, etc.
         */
        useEffectUnsafe(() => {
            return () => {
                if (isRenaming && saveOnBlur) confirmRenamingRef.current()
            }
        }, [isRenaming])

        return (
            <div
                className={classNames(
                    "flex items-center min-w-0 border-b gap-1",
                    className,
                    {
                        "border-white-300": isRenaming && !underlineClassNames,
                        "border-transparent": !isRenaming && !underlineClassNames,
                        "w-full": isFullWidth,
                        "select-none": !shouldMakeTextSelectable,
                        "-mt-1 -mb-2": shouldHighlightOnHover,
                        "mt-px": !shouldHighlightOnHover,
                    },
                    underlineClassNames?.(isRenaming)
                )}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                onClick={onClick}
            >
                <div
                    className={classNames("flex", {
                        "px-1.5 py-1 rounded-md -ml-1.5 -mt-1": shouldHighlightOnHover,
                        "bg-gray-500": isHovering && !isRenaming && shouldHighlightOnHover,
                        "-mr-1.5": !isRenaming && shouldHighlightOnHover,
                        "flex-grow overflow-hidden": isFullWidth,
                    })}
                >
                    <Tag
                        className={classNames("flex items-center max-w-full", {
                            "w-full": isFullWidth,
                        })}
                    >
                        {!isRenaming ? (
                            <>
                                <span
                                    className={classNames("overflow-hidden min-w-0", {
                                        "text-ellipsis whitespace-nowrap": maxLines === 1,
                                        [`line-clamp-${maxLines} block`]: maxLines > 1,
                                    })}
                                >
                                    {t(title)}
                                </span>
                                {suffix && (
                                    <span className="flex-none whitespace-nowrap ml-1">
                                        {suffix}
                                    </span>
                                )}
                            </>
                        ) : (
                            <input
                                className={classNames(
                                    "p-0 bg-transparent border-0 !shadow-none min-w-0 w-full flex-grow leading-[inherit]",
                                    "focus:outline-none focus:border-transparent focus:ring-0",
                                    {
                                        "w-full": isFullWidth,
                                    }
                                )}
                                style={{ fontSize: "inherit" }}
                                ref={inputRef}
                                type="text"
                                value={renamedTitle}
                                onChange={(e) => {
                                    let sanitizedTitle: string | null = e.target.value.trimStart()
                                    if (sanitizer) sanitizedTitle = sanitizer(sanitizedTitle)
                                    if (sanitizedTitle === null) return
                                    setRenamedTitle(sanitizedTitle)
                                }}
                                onKeyDown={handleKeyDown}
                                placeholder={initialTitleIsPlaceholder ? title : undefined}
                                autoFocus
                            />
                        )}
                    </Tag>
                </div>

                {isRenaming && (
                    <ConfirmControls
                        isValid={true}
                        iconClassName={classNames({
                            "hover:bg-gray-500": backgroundIsDark,
                            "hover:bg-gray-700": !backgroundIsDark,
                        })}
                        onConfirm={() => {
                            const sanitizedTitle = renamedTitle.trimEnd()
                            if (sanitizedTitle.length === 0) {
                                onCancelRenaming?.()
                                return
                            }
                            onConfirmRenaming?.(id, sanitizedTitle)
                            setRenamedTitle(title)
                            setIsHovering(false)
                        }}
                        onCancel={() => {
                            onCancelRenaming?.()
                            setRenamedTitle(title)
                            setIsHovering(false)
                        }}
                        onReset={
                            onResetName
                                ? () => {
                                      onResetName()
                                      setIsHovering(false)
                                  }
                                : undefined
                        }
                    />
                )}
            </div>
        )
    }
)
RenamableText.displayName = "RenamableText"
export { RenamableText }
